import { router } from '@inertiajs/svelte'
import type { SiteCartStore } from '~/lib/site-cart-store.svelte'
import { get } from 'svelte/store'
import { page } from '@inertiajs/svelte'
import { lineQtyFromCart, readServerSiteCart, resolveSiteCart } from '~/lib/site-cart-view'
import type { SiteCatalogProduct, SiteCartSnapshot } from '~/lib/site-catalog'
import { siteCartStore, syncSiteCartFromServer } from '~/lib/site-cart-store.svelte'

function currentPageProps(): Record<string, unknown> | null {
  const currentPage = get(page)
  if (!currentPage?.props) {
    return null
  }
  return currentPage.props as Record<string, unknown>
}

function currentCart(): SiteCartSnapshot {
  const props = currentPageProps()
  if (!props) {
    return siteCartStore.snapshot
  }
  return resolveSiteCart(siteCartStore.snapshot, props)
}

/** CSRF из cookie Adonis Shield (как в webapp) */
export function getSiteCsrfToken(): string {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'XSRF-TOKEN') {
      return decodeURIComponent(value)
    }
  }
  return ''
}

export interface SyncSiteCartOptions {
  /** Открыть drawer после успешного добавления (не при удалении) */
  openDrawer?: boolean
  onSuccess?: () => void
}

/**
 * Синхронизация корзины: сначала оптимистичный UI, затем POST JSON без Inertia reload.
 */
export async function syncSiteCart(
  store: SiteCartStore,
  product: SiteCatalogProduct,
  quantity: number,
  options: SyncSiteCartOptions = {}
): Promise<boolean> {
  const previous = store.cloneSnapshot()
  store.applyOptimistic(product, quantity)

  const token = getSiteCsrfToken()

  try {
    const response = await fetch('/cart', {
      method: 'POST',
      redirect: 'manual',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-XSRF-TOKEN': token,
      },
      body: JSON.stringify({ productId: product.id, quantity }),
    })

    if (response.status === 302 || response.status === 301) {
      store.rollback(previous)
      const location = response.headers.get('Location') ?? ''
      if (location.includes('/login')) {
        router.visit('/login')
        return false
      }
      if (location.includes('/account') || location.includes('/contacts')) {
        router.visit(location)
        return false
      }
      return false
    }

    if (response.status === 401 || response.status === 403) {
      store.rollback(previous)
      router.visit('/login')
      return false
    }

    const payload = (await response.json().catch(() => null)) as {
      success?: boolean
      message?: string
      siteCart?: SiteCartSnapshot
    } | null

    if (!response.ok || !payload?.success || !payload.siteCart) {
      store.rollback(previous)
      return false
    }

    store.applyServer(payload.siteCart)

    if (options.openDrawer && quantity > 0 && payload.siteCart.count > 0) {
      options.onSuccess?.()
    }

    return true
  } catch {
    store.rollback(previous)
    return false
  }
}

/** +1 кг к позиции (или первая единица) */
export function addOneToSiteCart(
  store: SiteCartStore,
  product: SiteCatalogProduct,
  options?: SyncSiteCartOptions
): Promise<boolean> {
  const next = lineQtyFromCart(currentCart(), product.id) + 1
  if (next > product.quantity || product.quantity <= 0) {
    return Promise.resolve(false)
  }
  return syncSiteCart(store, product, next, options)
}

/** Изменение на delta (−1 / +1) */
export function changeSiteCartQty(
  store: SiteCartStore,
  product: SiteCatalogProduct,
  delta: number,
  options?: SyncSiteCartOptions
): Promise<boolean> {
  const next = lineQtyFromCart(currentCart(), product.id) + delta
  if (next < 0) {
    return Promise.resolve(false)
  }
  const quantity = next === 0 ? 0 : Math.min(next, product.quantity)
  return syncSiteCart(store, product, quantity, options)
}

/** Подгрузка корзины с API, если shared props пустые (после F5) */
export async function fetchSiteCartSnapshot(
  overrideProps?: Record<string, unknown>
): Promise<void> {
  const props = overrideProps ?? currentPageProps()
  if (!props) {
    return
  }
  const customer = props.siteCustomer as { approvalStatus?: string } | null | undefined
  if (customer?.approvalStatus !== 'approved') {
    return
  }

  if (resolveSiteCart(siteCartStore.snapshot, props).count > 0) {
    return
  }

  const token = getSiteCsrfToken()

  try {
    const response = await fetch('/cart/snapshot', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-XSRF-TOKEN': token,
      },
    })

    if (!response.ok) {
      return
    }

    const payload = (await response.json()) as { siteCart?: SiteCartSnapshot }
    if (payload.siteCart && payload.siteCart.count > 0) {
      syncSiteCartFromServer(payload.siteCart)
    }
  } catch {
    // сеть / авторизация — оставляем пустую корзину
  }
}
